import { useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { updateAccountPreferences } from '@/lib/api'
import { clonePresetValue, MAX_PRESETS, MAX_PRESET_NAME_LENGTH, presetFingerprint, readNamedPresets, type NamedPreset, type PresetPreferenceKey } from '@/lib/namedPresets'
import type { User } from '@/types'

export interface NamedPresetControls {
  scope: string
  items: { id: string; name: string }[]
  selectedId: string
  modified: boolean
  busy: boolean
  disabled: boolean
  error: string | null
  status: string | null
  onSelect: (id: string) => Promise<boolean>
  onCreate: (name: string) => Promise<boolean>
  onUpdate: () => Promise<boolean>
  onRename: (name: string) => Promise<boolean>
  onDelete: () => Promise<boolean>
}

interface Options<T> {
  user: User | null
  preferenceKey: PresetPreferenceKey
  value: T
  onApply: (value: T) => void | boolean | Promise<void | boolean>
  disabled?: boolean
  normalize?: (value: unknown) => T | null
}

/** Explicit snapshots never change when the user experiments with live settings. */
export function useNamedPresets<T>({ user, preferenceKey, value, onApply, disabled = false, normalize }: Options<T>): NamedPresetControls {
  const client = useQueryClient()
  const username = user?.username ?? null
  const scope = JSON.stringify([username, preferenceKey])
  const activeScope = useRef(scope)
  activeScope.current = scope
  const generation = useRef(0)
  const pending = useRef(false)
  const [busy, setBusy] = useState(false)
  const [chosenId, setChosenId] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const raw = user?.preferences?.[preferenceKey]
  const items = useMemo(() => readNamedPresets<T>(raw, normalize), [raw, normalize])
  const snapshot = normalize ? normalize(value) : value
  const fingerprint = presetFingerprint(snapshot)
  const matching = items.find((item) => presetFingerprint(item.value) === fingerprint)
  const selected = items.find((item) => item.id === chosenId) ?? matching

  useEffect(() => {
    generation.current += 1
    pending.current = false
    setBusy(false)
    setChosenId('')
    setError(null)
    setStatus(null)
    return () => { generation.current += 1 }
  }, [scope])

  // Remember an initial match so subsequent edits remain attached to that preset
  // and can be updated or reapplied, including after a page reload.
  useEffect(() => {
    if (!chosenId && matching && !disabled) setChosenId(matching.id)
  }, [chosenId, matching, disabled])

  const run = async (operation: (isCurrent: () => boolean) => Promise<void>): Promise<boolean> => {
    if (!username || disabled || pending.current || activeScope.current !== scope) return false
    const request = ++generation.current
    const isCurrent = () => activeScope.current === scope && generation.current === request
    pending.current = true
    setBusy(true)
    setError(null)
    setStatus(null)
    try {
      await operation(isCurrent)
      return isCurrent()
    } catch (cause) {
      if (isCurrent()) setError(cause instanceof Error ? cause.message : 'Could not save the preset. Please try again.')
      return false
    } finally {
      if (isCurrent()) {
        pending.current = false
        setBusy(false)
      }
    }
  }

  const persist = async (next: NamedPreset<T>[], isCurrent: () => boolean, id: string, message: string) => {
    const preferences = { [preferenceKey]: next }
    const result = await updateAccountPreferences(preferences)
    if (!isCurrent()) return
    if (!result.ok) throw new Error(result.error || 'Could not save the preset. Please try again.')
    client.setQueryData<User | null>(['auth', 'me'], (current) => current?.username === username
      ? { ...current, preferences: { ...current.preferences, ...preferences } }
      : current)
    setChosenId(id)
    setStatus(message)
  }

  const checkedName = (name: string, exceptId?: string) => {
    const trimmed = name.trim()
    if (!trimmed || trimmed.length > MAX_PRESET_NAME_LENGTH) throw new Error('Enter a preset name between 1 and 80 characters.')
    if (items.some((item) => item.id !== exceptId && item.name.toLowerCase() === trimmed.toLowerCase())) {
      throw new Error('A preset with this name already exists. Choose another name or update that preset.')
    }
    return trimmed
  }

  const currentSnapshot = () => {
    if (snapshot == null) throw new Error('These settings are not ready to save yet.')
    return clonePresetValue(snapshot)
  }

  return {
    scope, items, selectedId: selected?.id ?? '',
    modified: !!selected && presetFingerprint(selected.value) !== fingerprint,
    busy, disabled: disabled || !username, error, status,
    onSelect: (id) => run(async (isCurrent) => {
      const preset = items.find((item) => item.id === id)
      if (!preset) throw new Error('This preset is no longer available.')
      const ok = await onApply(clonePresetValue(preset.value))
      if (ok === false) throw new Error('Could not apply the preset. Please try again.')
      if (isCurrent()) {
        setChosenId(id)
        setStatus(`Applied “${preset.name}”.`)
      }
    }),
    onCreate: (name) => run(async (isCurrent) => {
      if (items.length >= MAX_PRESETS) throw new Error(`You can save up to ${MAX_PRESETS} presets here. Delete one to make room.`)
      const preset = { id: crypto.randomUUID(), name: checkedName(name), value: currentSnapshot() }
      await persist([...items, preset], isCurrent, preset.id, `Saved “${preset.name}”.`)
    }),
    onUpdate: () => run(async (isCurrent) => {
      if (!selected) throw new Error('Choose a preset to update.')
      const nextValue = currentSnapshot()
      await persist(items.map((item) => item.id === selected.id ? { ...item, value: nextValue } : item),
        isCurrent, selected.id, `Updated “${selected.name}”.`)
    }),
    onRename: (name) => run(async (isCurrent) => {
      if (!selected) throw new Error('Choose a preset to rename.')
      const nextName = checkedName(name, selected.id)
      await persist(items.map((item) => item.id === selected.id ? { ...item, name: nextName } : item),
        isCurrent, selected.id, `Renamed to “${nextName}”.`)
    }),
    onDelete: () => run(async (isCurrent) => {
      if (!selected) throw new Error('Choose a preset to delete.')
      await persist(items.filter((item) => item.id !== selected.id), isCurrent, '', `Deleted “${selected.name}”. Current settings are unchanged.`)
    }),
  }
}
